<template>
  <b-modal
    ref="modal"
    :id="modalId"
    :title="title"
    size="xl"
    hide-footer
    centered
    body-class="modal-sigtap__body"
    modal-class="modal-sigtap"
    @hidden="onModalHidden"
  >
    <template #modal-header="{ close }">
      <div class="modal-sigtap__header">
        <div>
          <h5>SIGTAPS</h5>

          <p v-if="subtitle">
            {{ subtitle }}
          </p>
        </div>

        <button
          type="button"
          class="modal-sigtap__close"
          aria-label="Fechar"
          @click="close"
        >
          <i class="icofont-close-line"></i>
        </button>
      </div>
    </template>

    <section class="procedimentos-indicador">
      <div class="procedimentos-busca">
        <i class="icofont-search-1"></i>

        <input
          ref="campoBusca"
          v-model.trim="busca"
          type="text"
          placeholder="Pesquisar por código ou descrição..."
          aria-label="Pesquisar procedimentos SIGTAP"
        />

        <button
          v-if="busca"
          type="button"
          class="procedimentos-busca__limpar"
          title="Limpar pesquisa"
          @click="limparBusca"
        >
          <i class="icofont-close-line"></i>
        </button>
      </div>

      <div class="procedures-grid">
        <!-- Numerador -->
        <div class="proc-table">
          <div class="proc-header">
            <span class="proc-header__icon red">NM</span>

            <span class="proc-header__title">
              {{ numeradorLabel }}
            </span>

            <span class="proc-header__count">
              {{ procedimentosNumeradorFiltrados.length }}
            </span>
          </div>

          <div class="proc-body">
            <div
              v-for="(procedimento, index) in procedimentosNumeradorVisiveis"
              :key="getProcedimentoKey(procedimento, index, 'numerador')"
              class="proc-row"
            >
              <code
                v-if="procedimento.codigo != null"
                class="proc-code proc-code--red"
              >
                {{ procedimento.codigo }}
              </code>

              <span class="proc-desc">
                {{ getDescricao(procedimento) }}
              </span>
            </div>

            <div
              v-if="procedimentosNumeradorFiltrados.length === 0"
              class="proc-empty"
            >
              Nenhum procedimento encontrado.
            </div>

            <button
              v-if="mostrarToggleNumerador"
              type="button"
              class="proc-toggle"
              @click="expandidoNumerador = !expandidoNumerador"
            >
              {{
                expandidoNumerador
                  ? 'Mostrar menos ▲'
                  : `Ver todos os ${procedimentosNumeradorFiltrados.length} procedimentos ▼`
              }}
            </button>
          </div>
        </div>

        <!-- Denominador -->
        <div class="proc-table">
          <div class="proc-header">
            <span class="proc-header__icon">DN</span>

            <span class="proc-header__title">
              {{ denominadorLabel }}
            </span>

            <span class="proc-header__count">
              {{ procedimentosDenominadorFiltrados.length }}
            </span>
          </div>

          <div class="proc-body">
            <div
              v-for="(procedimento, index) in procedimentosDenominadorVisiveis"
              :key="getProcedimentoKey(procedimento, index, 'denominador')"
              class="proc-row"
            >
              <code
                v-if="procedimento.codigo != null"
                class="proc-code proc-code--blue"
              >
                {{ procedimento.codigo }}
              </code>

              <span class="proc-desc">
                {{ getDescricao(procedimento) }}
              </span>
            </div>

            <div
              v-if="procedimentosDenominadorFiltrados.length === 0"
              class="proc-empty"
            >
              Nenhum procedimento encontrado.
            </div>

            <button
              v-if="mostrarToggleDenominador"
              type="button"
              class="proc-toggle"
              @click="expandidoDenominador = !expandidoDenominador"
            >
              {{
                expandidoDenominador
                  ? 'Mostrar menos ▲'
                  : `Ver todos os ${procedimentosDenominadorFiltrados.length} procedimentos ▼`
              }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </b-modal>
</template>

<script>
export default {
  name: 'ModalSigtap',

  props: {
    modalId: {
      type: String,
      default: 'modal-sigtap',
    },

    title: {
      type: String,
      default: 'Procedimentos SIGTAP',
    },

    subtitle: {
      type: String,
      default: '',
    },

    numeradorLabel: {
      type: String,
      default: 'Numerador',
    },

    denominadorLabel: {
      type: String,
      default: 'Denominador',
    },

    procedimentosNumerador: {
      type: Array,
      default: () => [],
    },

    procedimentosDenominador: {
      type: Array,
      default: () => [],
    },

    limiteProcedimentos: {
      type: Number,
      default: 5,
    },
  },

  data() {
    return {
      busca: '',
      expandidoNumerador: false,
      expandidoDenominador: false,
    }
  },

  computed: {
    /**
     * Retorna os procedimentos do numerador filtrados pela pesquisa.
     *
     * @returns {Array}
     */
    procedimentosNumeradorFiltrados() {
      return this.filtrarProcedimentos(this.procedimentosNumerador)
    },

    /**
     * Retorna os procedimentos do denominador filtrados pela pesquisa.
     *
     * @returns {Array}
     */
    procedimentosDenominadorFiltrados() {
      return this.filtrarProcedimentos(this.procedimentosDenominador)
    },

    /**
     * Retorna os procedimentos visíveis na tabela do numerador.
     *
     * @returns {Array}
     */
    procedimentosNumeradorVisiveis() {
      return this.getProcedimentosVisiveis(
        this.procedimentosNumeradorFiltrados,
        this.expandidoNumerador
      )
    },

    /**
     * Retorna os procedimentos visíveis na tabela do denominador.
     *
     * @returns {Array}
     */
    procedimentosDenominadorVisiveis() {
      return this.getProcedimentosVisiveis(
        this.procedimentosDenominadorFiltrados,
        this.expandidoDenominador
      )
    },

    /**
     * Indica se o botão de expansão do numerador deve aparecer.
     *
     * @returns {boolean}
     */
    mostrarToggleNumerador() {
      return (
        !this.buscaNormalizada &&
        this.procedimentosNumeradorFiltrados.length > this.limiteProcedimentos
      )
    },

    /**
     * Indica se o botão de expansão do denominador deve aparecer.
     *
     * @returns {boolean}
     */
    mostrarToggleDenominador() {
      return (
        !this.buscaNormalizada &&
        this.procedimentosDenominadorFiltrados.length > this.limiteProcedimentos
      )
    },

    /**
     * Retorna o termo de busca normalizado.
     *
     * @returns {string}
     */
    buscaNormalizada() {
      return this.normalizar(this.busca)
    },
  },

  watch: {
    /**
     * Recolhe as duas tabelas sempre que a busca for alterada.
     *
     * @returns {void}
     */
    busca() {
      this.expandidoNumerador = false
      this.expandidoDenominador = false
    },
  },

  methods: {
    /**
     * Abre o modal e posiciona o foco no campo de busca.
     *
     * @returns {void}
     */
    open() {
      this.$refs.modal.show()

      this.$nextTick(() => {
        window.setTimeout(() => {
          this.$refs.campoBusca?.focus()
        }, 200)
      })
    },

    /**
     * Fecha o modal.
     *
     * @returns {void}
     */
    close() {
      this.$refs.modal.hide()
    },

    /**
     * Limpa o estado interno depois que o modal for fechado.
     *
     * @returns {void}
     */
    onModalHidden() {
      this.busca = ''
      this.expandidoNumerador = false
      this.expandidoDenominador = false
    },

    /**
     * Filtra procedimentos por código ou descrição.
     *
     * @param {Array} procedimentos
     * @returns {Array}
     */
    filtrarProcedimentos(procedimentos) {
      const lista = Array.isArray(procedimentos) ? procedimentos : []

      if (!this.buscaNormalizada) {
        return lista
      }

      return lista.filter((procedimento) => {
        const codigo = String(procedimento?.codigo || '')
        const descricao = this.getDescricao(procedimento)
        const conteudo = this.normalizar(`${codigo} ${descricao}`)

        return conteudo.includes(this.buscaNormalizada)
      })
    },

    /**
     * Retorna os procedimentos que devem ser apresentados.
     *
     * Durante uma pesquisa, todos os resultados encontrados são exibidos.
     *
     * @param {Array} procedimentos
     * @param {boolean} expandido
     * @returns {Array}
     */
    getProcedimentosVisiveis(procedimentos, expandido) {
      if (this.buscaNormalizada || expandido) {
        return procedimentos
      }

      return procedimentos.slice(0, this.limiteProcedimentos)
    },

    /**
     * Retorna a descrição disponível no procedimento.
     *
     * @param {Object} procedimento
     * @returns {string}
     */
    getDescricao(procedimento) {
      return String(
        procedimento?.desc ||
          procedimento?.descricao ||
          procedimento?.nome ||
          ''
      )
    },

    /**
     * Gera a chave utilizada na renderização dos procedimentos.
     *
     * @param {Object} procedimento
     * @param {number} index
     * @param {string} tipo
     * @returns {string}
     */
    getProcedimentoKey(procedimento, index, tipo) {
      const codigo = String(procedimento?.codigo || '')
      const descricao = this.getDescricao(procedimento)

      return `${tipo}-${codigo}-${descricao}-${index}`
    },

    /**
     * Limpa o campo de pesquisa.
     *
     * @returns {void}
     */
    limparBusca() {
      this.busca = ''

      this.$nextTick(() => {
        this.$refs.campoBusca?.focus()
      })
    },

    /**
     * Normaliza um valor para comparação textual.
     *
     * @param {*} valor
     * @returns {string}
     */
    normalizar(valor) {
      return String(valor || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
    },
  },
}
</script>

<style scoped lang="scss">
.modal-sigtap__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;

  h5 {
    margin: 0;
    color: #101828;
    font-size: 1.1rem;
    font-weight: 700;
  }

  p {
    margin: 0.25rem 0 0;
    color: #667085;
    font-size: 0.82rem;
  }
}

.modal-sigtap__close {
  display: grid;
  width: 34px;
  height: 34px;
  padding: 0;
  color: #667085;
  background: transparent;
  border: 0;
  border-radius: 50%;
  place-items: center;

  &:hover {
    color: #344054;
    background: #f2f4f7;
  }
}

.procedimentos-indicador {
  padding: 0.5rem 0;
}

.procedimentos-busca {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 520px;
  margin-bottom: 1rem;

  > i {
    position: absolute;
    left: 14px;
    color: #667085;
    pointer-events: none;
  }

  input {
    width: 100%;
    min-height: 42px;
    padding: 0.65rem 2.75rem 0.65rem 2.5rem;
    color: #344054;
    font-size: 0.875rem;
    background: #fff;
    border: 1px solid #d0d5dd;
    border-radius: 10px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &::placeholder {
      color: #98a2b3;
    }

    &:focus {
      border-color: #2e70d9;
      box-shadow: 0 0 0 3px rgba(46, 112, 217, 0.12);
    }
  }

  &__limpar {
    position: absolute;
    right: 10px;
    display: grid;
    width: 26px;
    height: 26px;
    padding: 0;
    color: #667085;
    background: transparent;
    border: 0;
    border-radius: 50%;
    place-items: center;

    &:hover {
      color: #344054;
      background: #f2f4f7;
    }
  }
}

.procedures-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.proc-table {
  overflow: hidden;
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}

.proc-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #f9fafb 0%, #fff 100%);
  border-bottom: 1px solid #f2f4f7;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 27px;
    height: 27px;
    color: #fff;
    font-size: 12px;
    background: #2e70d9;
    border-radius: 6px;

    &.red {
      background: #f04438;
    }
  }

  &__title {
    color: #1d2939;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  &__count {
    padding: 2px 8px;
    margin-left: auto;
    color: #667085;
    font-size: 11px;
    font-weight: 600;
    background: #f2f4f7;
    border-radius: 20px;
  }
}

.proc-body {
  max-height: 420px;
  padding: 4px 0;
  overflow-y: auto;
}

.proc-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 20px;
  border-bottom: 1px solid #f9fafb;

  &:last-child {
    border-bottom: 0;
  }
}

.proc-code {
  flex-shrink: 0;
  padding: 2px 6px;
  font-family: monospace;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  border-radius: 6px;

  &--red {
    color: #b42318;
    background: #fef3f2;
  }

  &--blue {
    color: #2e70d9;
    background: rgba(46, 112, 217, 0.08);
  }
}

.proc-desc {
  color: #475467;
  font-size: 13px;
  line-height: 1.4;
}

.proc-empty {
  padding: 2rem 1rem;
  color: #98a2b3;
  font-size: 13px;
  text-align: center;
}

.proc-toggle {
  width: 100%;
  padding: 14px 20px 16px;
  color: #2e70d9;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #f9fafb;
  }
}

@media (max-width: 768px) {
  .procedures-grid {
    grid-template-columns: 1fr;
  }

  .procedimentos-busca {
    max-width: none;
  }
}
</style>
